import React, { useCallback, useEffect, useState, type KeyboardEvent } from "react";
import { LoadingOutlined, PlusOutlined, RobotOutlined, StopOutlined, ToolOutlined } from "@ant-design/icons";
import { getToolName, isToolUIPart, type DynamicToolUIPart, type ToolUIPart, type UIMessage } from "ai";
import { Button, Drawer, FloatButton, Space, Tag, Typography } from "antd";

import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@fitness-app/web-ui/components/ai-elements/conversation";
import { Loader } from "@fitness-app/web-ui/components/ai-elements/loader";
import { Message, MessageContent } from "@fitness-app/web-ui/components/ai-elements/message";
import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputTextarea,
  PromptInputTools,
} from "@fitness-app/web-ui/components/ai-elements/prompt-input";

import { SalesAgentMessageParts, SalesAgentPromptSubmit } from "~/modules/Sales/SalesAgent/SalesAgentMessageParts";

import { useStudioAdsChat } from "../hooks/useStudioAdsChat";

const SUGGESTIONS = [
  "Pokaż mi najlepsze hooki z tego nagrania.",
  "Wypisz 3 najbardziej konkretne value.",
  "Złóż warianty z dwóch najlepszych hooków, trzech value i CTA.",
];

const TOOL_LABELS: Record<string, string> = {
  listStudioSegments: "Przeglądam klocki",
  composeStudioVariants: "Składam warianty",
  listStudioVariants: "Sprawdzam warianty",
  renderStudioVariants: "Uruchamiam render",
};

const ToolPart = ({ part }: { part: ToolUIPart | DynamicToolUIPart }): React.ReactElement => {
  const failed = part.state === "output-error" || part.state === "output-denied";
  const done = part.state === "output-available";
  const name = getToolName(part);
  return (
    <div
      className={`my-2 rounded-lg border px-3 py-2 text-xs ${failed ? "border-red-200 bg-red-50" : "border-gray-200 bg-gray-50"}`}
    >
      <Space>
        <ToolOutlined />
        <span>{TOOL_LABELS[name] ?? name}</span>
        <Tag color={failed ? "red" : done ? "green" : "blue"}>{done ? "Gotowe" : failed ? "Błąd" : "Pracuję"}</Tag>
      </Space>
    </div>
  );
};

const AdsMessageParts = ({
  message,
  isLastMessage,
  streaming,
}: {
  message: UIMessage;
  isLastMessage: boolean;
  streaming: boolean;
}): React.ReactElement => (
  <>
    {message.parts.filter(isToolUIPart).map((part, index) => (
      <ToolPart key={`${message.id}-tool-${index}`} part={part} />
    ))}
    <SalesAgentMessageParts message={message} isLastMessage={isLastMessage} isStreaming={streaming} />
  </>
);

/**
 * Czat nad panelem Reklamy (etap G) w szufladce, jak agent planów treningowych: te same
 * klocki i te same akcje, co przyciski — „znajdź hooki", „złóż warianty", „wyrenderuj dwa
 * pierwsze". Odpowiedź streamuje się razem z krokami narzędzi, a efekt każdej akcji agenta
 * pokazuje się w tabeli wariantów pod spodem, więc rozmowa i klikanie to jedna prawda.
 * Rozmowa zostaje przy nagraniu; zamknięta szufladka w trakcie odpowiedzi zostawia pływający
 * przycisk, żeby dało się wrócić.
 */
export const AdsChatDrawer = ({
  requestId,
  open,
  onClose,
  onOpen,
}: {
  requestId: string | null;
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
}): React.ReactElement => {
  const chat = useStudioAdsChat(requestId);
  const [input, setInput] = useState("");
  const [hasUnread, setHasUnread] = useState(false);

  useEffect(() => {
    if (open) setHasUnread(false);
  }, [open]);
  useEffect(() => {
    if (!open && chat.status === "ready" && chat.messages.length) setHasUnread(true);
    // Only a finished reply should light the badge, not every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.status]);

  const submit = useCallback(() => {
    const content = input.trim();
    if (!content || chat.busy) return;
    setInput("");
    chat.send(content);
  }, [chat, input]);

  const handlePromptKeyDown = useCallback((event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    event.currentTarget.form?.requestSubmit();
  }, []);

  const showFab = !open && (chat.busy || hasUnread);

  return (
    <>
      {showFab ? (
        <FloatButton
          type="primary"
          icon={chat.busy ? <LoadingOutlined /> : <RobotOutlined />}
          badge={chat.busy ? { dot: true, color: "gold" } : { dot: true, color: "green" }}
          tooltip={
            chat.busy ? "Agent przygotowuje odpowiedź — kliknij, aby wrócić do rozmowy" : "Nowa odpowiedź agenta"
          }
          onClick={onOpen}
        />
      ) : null}
      <Drawer
        open={open}
        onClose={onClose}
        width="min(900px, 96vw)"
        title="Agent reklam"
        destroyOnClose={false}
        extra={
          <Button size="small" icon={<PlusOutlined />} onClick={chat.reset} disabled={!chat.messages.length}>
            Nowa rozmowa
          </Button>
        }
      >
        <div className="flex h-[calc(100vh-150px)] min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200">
          <Conversation>
            <ConversationContent className="mx-auto w-full max-w-3xl px-5 py-5">
              {chat.messages.length ? (
                chat.messages.map((message, index) => (
                  <Message key={message.id} from={message.role}>
                    <MessageContent
                      className={
                        message.role === "user"
                          ? "rounded-[18px] bg-gray-100 px-4 py-2.5"
                          : "border-transparent bg-transparent px-0 py-0 shadow-none"
                      }
                    >
                      <AdsMessageParts
                        message={message}
                        isLastMessage={index === chat.messages.length - 1}
                        streaming={chat.status === "streaming" && index === chat.messages.length - 1}
                      />
                    </MessageContent>
                  </Message>
                ))
              ) : (
                <ConversationEmptyState
                  icon={<RobotOutlined className="text-2xl" />}
                  title="Złóż reklamy rozmową"
                  description="Agent widzi te same hooki, value i CTA, co panel, i składa z nich warianty albo wysyła je do renderu. Zacznij od jednej z podpowiedzi."
                >
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    {SUGGESTIONS.map((suggestion) => (
                      <Button
                        key={suggestion}
                        size="small"
                        type="dashed"
                        disabled={!requestId || chat.busy}
                        onClick={() => chat.send(suggestion)}
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                </ConversationEmptyState>
              )}
              {chat.status === "submitted" ? <Loader /> : null}
              {chat.error ? (
                <Typography.Text type="danger">Nie udało się odpowiedzieć: {chat.error.message}</Typography.Text>
              ) : null}
            </ConversationContent>
            <ConversationScrollButton />
          </Conversation>

          <div className="shrink-0 px-4 pb-4 pt-0">
            <PromptInput
              onSubmit={submit}
              className="relative mx-auto max-w-3xl rounded-[28px] border-gray-200 bg-white/95 shadow-[0_12px_40px_rgba(15,23,42,0.08)] backdrop-blur-xl"
            >
              <PromptInputBody>
                <PromptInputTextarea
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={handlePromptKeyDown}
                  placeholder="Napisz, co złożyć albo o co zapytać…"
                  disabled={!requestId || chat.busy}
                />
              </PromptInputBody>
              <PromptInputFooter>
                <PromptInputTools />
                {chat.busy ? (
                  <Button
                    className="h-10 w-10 rounded-full bg-gray-950 text-white hover:bg-gray-800"
                    icon={<StopOutlined />}
                    onClick={chat.stop}
                  >
                    <span className="sr-only">Stop</span>
                  </Button>
                ) : (
                  <SalesAgentPromptSubmit input={input} status={chat.status} />
                )}
              </PromptInputFooter>
            </PromptInput>
          </div>
        </div>
      </Drawer>
    </>
  );
};
